import { useEffect, useMemo, useState } from "react";
import { techGraph, groupColors } from "@/data/portfolio";
import { Reveal } from "@/lib/motion";
import { SectionHeader } from "@/components/About";
import { TID } from "@/constants/testIds";

const SX = 10; // x scale: data (0..100) -> viewBox (0..1000)
const VB_W = 1000;

export function TechGraph() {
  const [hover, setHover] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [canHover, setCanHover] = useState(true);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 767px)");
    const hoverMq = window.matchMedia("(hover: hover) and (pointer: fine)");
    const sync = () => setIsMobile(mq.matches);
    const syncHover = () => setCanHover(hoverMq.matches);
    sync();
    syncHover();
    mq.addEventListener?.("change", sync);
    hoverMq.addEventListener?.("change", syncHover);
    return () => {
      mq.removeEventListener?.("change", sync);
      hoverMq.removeEventListener?.("change", syncHover);
    };
  }, []);

  const graphSY = isMobile ? 6.2 : 4.8;
  const VB_H =
    Math.ceil(
      Math.max(
        ...techGraph.nodes.map((n) => n.y * graphSY + (n.hub ? (isMobile ? 38 : 30) : isMobile ? 30 : 24)),
      ),
    ) + 16;

  const { nodeById, neighbors } = useMemo(() => {
    const map = {};
    techGraph.nodes.forEach((n) => (map[n.id] = n));
    const nb = {};
    techGraph.nodes.forEach((n) => (nb[n.id] = new Set([n.id])));
    techGraph.edges.forEach(([a, b]) => {
      nb[a].add(b);
      nb[b].add(a);
    });
    return { nodeById: map, neighbors: nb };
  }, []);

  const interactive = canHover && !isMobile;
  const activeSet = interactive && hover ? neighbors[hover] : null;
  const isDim = (id) => activeSet && !activeSet.has(id);
  return (
    <section id="stack" data-testid={TID.stack} className="mx-auto max-w-6xl px-5 py-20 sm:px-8 sm:py-24">
      <div className="grid gap-8 lg:grid-cols-[minmax(0,0.56fr)_minmax(0,1.44fr)] lg:items-start">
        <div className="mx-auto max-w-lg self-start lg:mx-0 lg:sticky lg:top-28">
          <SectionHeader label="02 / stack" title="A living architecture, not a résumé." />

          <Reveal className="mx-auto max-w-md text-base leading-relaxed text-muted-foreground sm:text-lg lg:mx-0">
            Not a list of logos, but a map of the technologies I use to build production systems.
          </Reveal>
        </div>

        <Reveal
          className="relative mx-auto flex w-full max-w-2xl flex-col self-start overflow-hidden rounded-2xl border border-border bg-card/40 p-1.5 sm:p-3 lg:mx-0 lg:max-w-none"
          data-testid="tech-graph"
        >
          <svg
            viewBox={`0 0 ${VB_W} ${VB_H}`}
            className={`h-[410px] w-full select-none sm:h-[330px] lg:h-[318px] ${interactive ? "touch-pan-y lg:touch-none" : "pointer-events-none touch-none"}`}
            role="img"
            aria-label="Interactive graph of technologies and how they connect"
          >
            {/* Edges */}
            {techGraph.edges.map(([a, b], i) => {
              const na = nodeById[a];
              const nb = nodeById[b];
              const incident = interactive && hover && (a === hover || b === hover);
              const dim = activeSet && !incident;
              return (
                <line
                  key={i}
                  x1={na.x * SX} y1={na.y * graphSY} x2={nb.x * SX} y2={nb.y * graphSY}
                  stroke={incident ? "hsl(var(--primary))" : "hsl(var(--border))"}
                  strokeWidth={incident ? 2 : 1}
                  className={incident ? "dash-flow" : ""}
                  style={{ opacity: dim ? 0.12 : incident ? 0.95 : 0.5, transition: "opacity 0.3s ease, stroke 0.3s ease" }}
                />
              );
            })}

            {/* Nodes */}
            {techGraph.nodes.map((n, i) => {
              const color = `hsl(${groupColors[n.group] || "82 80% 58%"})`;
              const dim = isDim(n.id);
              const isHub = n.hub;
              const active = hover === n.id;
              return (
                <g
                  key={n.id}
                  transform={`translate(${n.x * SX}, ${n.y * graphSY})`}
                  onMouseEnter={interactive ? () => setHover(n.id) : undefined}
                  onMouseLeave={interactive ? () => setHover(null) : undefined}
                  onFocus={interactive ? () => setHover(n.id) : undefined}
                  onBlur={interactive ? () => setHover(null) : undefined}
                  tabIndex={interactive ? 0 : -1}
                  data-testid={`tech-node-${n.id}`}
                  style={{
                    cursor: interactive ? "pointer" : "default",
                    opacity: dim ? 0.28 : 1,
                    transition: "opacity 0.3s ease",
                    outline: "none",
                  }}
                >
                  {active && (
                    <circle r={isMobile ? (isHub ? 40 : 30) : isHub ? 34 : 26} fill={color} opacity="0.16" />
                  )}
                  <circle
                    r={isMobile ? (isHub ? 20 : 14) : isHub ? 16 : 11}
                    fill={color}
                    className="node-breathe"
                    style={{ animationDelay: `${(i % 8) * 0.35}s` }}
                  />
                  <circle
                    r={isMobile ? (isHub ? 20 : 14) : isHub ? 16 : 11}
                    fill="none"
                    stroke={color}
                    strokeWidth="1"
                    opacity="0.5"
                  />
                  <text
                    x="0"
                    y={isMobile ? (isHub ? 44 : 36) : isHub ? 36 : 30}
                    textAnchor="middle"
                    fontSize={isMobile ? (isHub ? 20 : 15) : isHub ? 18 : 14}
                    fontFamily="JetBrains Mono, monospace"
                    fill={active ? color : "hsl(var(--muted-foreground))"}
                    style={{ transition: "fill 0.3s ease" }}
                  >
                    {n.label}
                  </text>
                </g>
              );
            })}
          </svg>

          {/* Legend */}
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 border-t border-border/60 pt-3 sm:flex">
            {[
              ["backend", "Backend"], ["ai", "AI / ML"], ["distributed", "Distributed"],
              ["cloud", "Cloud / DevOps"], ["observability", "Observability"], ["frontend", "Frontend"],
            ].map(([g, label]) => (
              <span key={g} className="flex items-center gap-2 font-mono text-[0.68rem] text-muted-foreground">
                <span className="h-2 w-2 rounded-full" style={{ background: `hsl(${groupColors[g]})` }} />
                {label}
              </span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
